import * as React from 'react';
import { FeedbackDialog } from './Dialog';
import {
    Widget,
    getWidgetProps,
    WPSClient,
    Args,
    withArgs,
    FeedbackProxyOutput
} from 'DVWebNodeUtils';
import { resetCounter, counterEligibility, hasAnswered, setAnswered } from './LocalStorageUtils'
import { FeedbackGetQuestionOutput } from '../model/model';


// We want to throttle incrementing the visit counter
// so that we don't increment it when a use opens
// different browse pages in different tabs etc.

const contextChangeEventType = "dvWebFeedbackContextChangeEvent"
const pageTypesToExcludeFromThrottling = ['detail']

interface FeedbackControllerState {
    isTriggered: boolean,
    ajaxProps: FeedbackGetQuestionOutput,
    context: PageTargetingContext
}

export interface FeedbackControllerProps {
    pageType: string,
    pageId: string,
    slotName: string,
    isDebug: boolean
}

interface PageTargetingContext {
    [key: string]: string
}

const getProps: getWidgetProps<FeedbackControllerProps> = global.__SERVER__ === true
    ? async ({ context,pageType, pageId, slotName }) => {
        const isDebug = context.isInternal &&
            context.queryParameters.hasOwnProperty("feedbackPageIdOverride")
        const pageIdProp = isDebug ?
            context.queryParameters.feedbackPageIdOverride[0] : pageId
        return { pageType: pageType, pageId: pageIdProp, slotName: slotName, isDebug: isDebug}
    }
    : undefined;


export class FeedbackControllerComponent extends React.Component<FeedbackControllerProps & Args, FeedbackControllerState> {
    submitAction: Function
    handleContextChangeAction: (e: CustomEvent) => void
    rootRef: React.RefObject<HTMLDivElement>
    constructor(props: FeedbackControllerProps & Args) {
        super(props)
        this.state = {
            isTriggered: this.props.isDebug,
            ajaxProps: null,
            context: {dwellTime: "0"}
        }
        this.rootRef = React.createRef();
        this.submitAction = this.submitAnswer.bind(this)
        this.handleContextChangeAction = this.handleContextChange.bind(this)
    }

    componentDidMount() {
        const { isDebug, pageType } = this.props
        this.rootRef.current.addEventListener(contextChangeEventType,
            this.handleContextChangeAction
        )
        const checkpoints = [5, 10, 15]
        for(const dwellCheckpoint of checkpoints) {
            setTimeout(() => this.updateDwellTime(dwellCheckpoint), dwellCheckpoint * 5000)
        }
        this.setState({
            isTriggered: isDebug || pageTypesToExcludeFromThrottling.includes(pageType) || counterEligibility(20)
        })
    }

    updateDwellTime(checkpoint: number) {
        this.setState(state => {
            if(checkpoint > Number(state.context.dwellTime) ) {
                return {
                ...state,
                context: {
                    ...state.context,
                    dwellTime: String(checkpoint)
                }
            }}
            else {
                return null
            }
        })
    }

    componentDidUpdate() {
        if (this.state.isTriggered && (!this.state.ajaxProps ||
                (this.state.ajaxProps && !this.state.ajaxProps.question))) {
                    this.getQuestion();
        }
    }

    componentWillUnmount(): void {
        this.rootRef &&
            this.rootRef.current &&
            this.rootRef.current.removeEventListener(contextChangeEventType, this.handleContextChangeAction)
    }

    submitAnswer(answerId: number) {
        //If submission failed, we still need the dialog to go away
        this.setState({ ...this.state, isTriggered: false })
        // Reset the counter -- mitigate possible issues around
        // eventual consistency, and block it for 3 visits if it failed.
        const api = new WPSClient()
        const output = this.state.ajaxProps
        setAnswered(output.question.questionId)
        resetCounter()
        api.FeedbackSubmitAnswer({
            context: this.props.context,
            questionId: output.question.questionId,
            answerId: answerId,
            token: output.token
        })
    }

    handleContextChange(e: CustomEvent) {
        e.detail.pageContext &&
        this.setState(state => {
            let different = false
            for (let key in e.detail.pageContext) {
                let valueNew = e.detail.pageContext[key];
                let valueOld = state.context[key];
                if (!state.context.hasOwnProperty(key) || valueNew !== valueOld) {
                    different = true
                }
            }
            if (different) {
                return {
                    ...state,
                    context: {
                        ...state.context,
                        ...e.detail.pageContext
                    }
                }
            }
        })
    }

    isVisible() {
        return (this.state.isTriggered &&
            this.state.ajaxProps &&
            this.state.ajaxProps.question &&
            !hasAnswered(this.state.ajaxProps.question.questionId));
    }

    getQuestion() {
        const api = new WPSClient()
        const pageTargetingCriteriaInput = JSON.stringify(this.state.context)
        const callBody = {
            context: this.props.context,
            pageType: this.props.pageType,
            pageId: this.props.pageId,
            slotName: this.props.slotName,
            pageTargetingCriteria: pageTargetingCriteriaInput,
        }
        api.FeedbackGetQuestion(callBody)
            .then((data: FeedbackProxyOutput) => {
                const ajaxProps: FeedbackGetQuestionOutput = JSON.parse(data.body)
                // This service being unavailable doesn't impact customer
                // CX so we can ignore it on the frontend with no retry
                if(ajaxProps.question && ajaxProps.token) {
                    this.setState({ ajaxProps: ajaxProps })
                }
            })
            .catch((reason: any) => {
                this.props.logger.warn(reason)
            })
    }

    render() {
        return (<div 
            id="PVWebFeedbackWidgetPubSub"
            ref={this.rootRef}>
            {this.isVisible() &&
                (
                    <FeedbackDialog question={this.state.ajaxProps.question}
                        submitAction={this.submitAction} />
                )
            }
        </div>);
    }
}

export const Feedback: Widget<FeedbackControllerProps> = {
    name: 'feedback',
    getProps,
    Component: withArgs(FeedbackControllerComponent) as React.ComponentType<FeedbackControllerProps>
}
